import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { Logger } from 'utils/Logger';

import {
  fundingSearchActive,
  fundingSearchCompleted,
  fundingSearchShared,
  fundingSearch,
  cancelFundingSearch,
  cancelFundingSearchNewHub,
  REQUEST_FUNDING_MATCHES_REQUIREMENTS,
  REQUEST_DOWNLOAD_PROFILE_ATTACHMENT,
  REQUEST_FUNDING_SEARCH_DOCUMENTS,
  deleteBusinessProfile,
  deleteBusinessProfileNewHub,
  requestFundingSearchHelp,
  dismissFundingSearchBanner,
  fundingSearchMatches,
  fundingSearchCompleteApplication,
} from './types';

import {
  requestFundingSearchActiveSuccess,
  requestFundingSearchActiveFailure,
  requestFundingSearchCompletedSuccess,
  requestFundingSearchCompletedFailure,
  requestFundingSearchSharedSuccess,
  requestFundingSearchSharedFailure,
  requestFundingSearchSuccess,
  requestFundingSearchFailure,
  requestFundingSearchCompleteApplicationFailure,
  requestFundingSearchCompleteApplicationSuccess,
  requestCancelFundingSearchSuccess,
  requestCancelFundingSearchFailure,
  closeCancelFundingSearchModal,
  requestDeleteBusinessProfileSuccess,
  requestDeleteBusinessProfileFailure,
  closeDeleteBusinessProfileModal,
  requestFundingSearchBannerDismiss,
  requestFundingSearchBannerDismissSuccess,
  requestFundingSearchBannerDismissFailure,
  requestFundingSearchMatchesSuccess,
  requestFundingSearchMatchesFailure,
} from './reducer';

import {
  requestDeleteBusinessProfileSuccess as casesRequestDeleteBusinessProfileSuccess,
  requestDeleteBusinessProfileFailure as casesRequestDeleteBusinessProfileFailure,
  requestCancelCaseSuccess,
  requestCancelCaseFailure,
} from '../cases/actions';

import { meRequest } from '../user/reducer';

import contactDetailsSaga from './contactDetails/sagas';
import dueActivitiesCountSaga from './dueActivitiesCount/sagas';
import matchSaga from './match/sagas';
import offersSaga from './offers/sagas';
import offersHistorySaga from './offersHistory/sagas';
import privacyToggleSaga from './privacyToggle/sagas';
import searchFollowersSaga from './searchFollowers/sagas';
import searchManagerSaga from './searchManager/sagas';

const logger = new Logger('Saga:FundingSearch');

export function* requestFundingSearchActiveSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.funding_search_active'),
    );

    yield put(requestFundingSearchActiveSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request active funding searches with the following error', error);
    yield put(requestFundingSearchActiveFailure(error));
  }
}

export function* requestFundingSearchCompletedSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.funding_search_completed'),
    );

    yield put(requestFundingSearchCompletedSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request completed funding searches with the following error', error);
    yield put(requestFundingSearchCompletedFailure(error));
  }
}

export function* requestFundingSearchSharedSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.funding_search_shared'),
    );

    yield put(requestFundingSearchSharedSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request shared funding searches with the following error', error);
    yield put(requestFundingSearchSharedFailure(error));
  }
}

export function* requestFundingSearchSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.platform.funding_search_by_id', {
        fundingSearchId: action.fundingSearchId,
        includeMatches: true,
        ...action.extraParams,
      }),
    );

    yield put(requestFundingSearchSuccess(action.fundingSearchId, response.data));
  } catch (error) {
    logger.error('Failed to request funding search with the following error', error);
    yield put(requestFundingSearchFailure(error));
  }
}

export function* requestFundingSearchCompleteApplicationSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.post,
      routerService.generate('api.questionnaireCompleteApplication'),
      action.payload,
    );

    yield put(requestFundingSearchCompleteApplicationSuccess());
  } catch (error) {
    logger.error('Failed to request to complete application with following error', error);
    yield put(requestFundingSearchCompleteApplicationFailure(error));
  }
}

export function* requestFundingSearchMatchesSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.platform.matches_by_funding_search_id', {
        fundingSearchId: action.fundingSearchId,
      }),
    );
    yield put(requestFundingSearchMatchesSuccess(action.fundingSearchId, response.data));
  } catch (error) {
    logger.error('Failed to request funding search matches with the following error', error);
    yield put(requestFundingSearchMatchesFailure(error));
  }
}

export function* requestCancelFundingSearchSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.cancel_funding_search', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestCancelFundingSearchSuccess(action.fundingSearchId, response.data));
    yield put(closeCancelFundingSearchModal());
    yield put(push('/funding'));
  } catch (error) {
    logger.error('Failed to request cancel funding search with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestCancelFundingSearchFailure(error));
  }
}

export function* requestCancelFundingSearchNewHubSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.cancel_funding_search', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestCancelCaseSuccess(action.fundingSearchId, response.data));
    yield put(push('/cases'));
  } catch (error) {
    logger.error('Failed to request cancel funding search with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestCancelCaseFailure(error));
  }
}

export function* requestDownloadProfileAttachmentSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.download_funding_search_profile_attachment', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    action.promise && action.promise.resolve(response.data.url);
  } catch (error) {
    logger.error('Failed to request download profile attachment with the following error', error);
    action.reject && action.promise.reject(error);
  }
}

export function* requestFundingSearchDocumentsSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.funding_search_request_documents', {
        fundingSearchId: action.fundingSearchId,
      }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to request funding search documents with the following error', error);
    action.reject && action.promise.reject(error);
  }
}

// only call this endpoint if coming from the new cases hub
// remove the second one after completly merge funding searches to the new hub
export function* requestDeleteBusinessProfileNewHubSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  try {
    const response = yield call(
      authApiService.delete,
      routerService.generate('api.platform.entity_all', { entityId: action.businessId }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(casesRequestDeleteBusinessProfileSuccess(action.businessId, response.data));
    yield put(push('/cases'));
  } catch (error) {
    logger.error('Failed to request delete profile with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(casesRequestDeleteBusinessProfileFailure(error));
  }
}

export function* requestDeleteBusinessProfileSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.delete_profile', { fundingSearchId: action.fundingSearchId }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestDeleteBusinessProfileSuccess(action.fundingSearchId, response.data));
    yield put(closeDeleteBusinessProfileModal());
    yield put(push('/funding'));
  } catch (error) {
    logger.error('Failed to request delete profile with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestDeleteBusinessProfileFailure(error));
  }
}

export function* requestFundingSearchBannerDismissSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.funding_search_banner_dismiss', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestFundingSearchBannerDismissSuccess(action.fundingSearchId, response.data));
    yield put(meRequest());
  } catch (error) {
    logger.error('Failed to request dismiss funding search banner with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestFundingSearchBannerDismissFailure(error));
  }
}

export function* requestFundingSearchRequestFundingSearchHelpSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.funding_search_call_me', {
        fundingSearchId: action.fundingSearchId,
      }),
      action.payload,
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestFundingSearchBannerDismiss(action.fundingSearchId));
  } catch (error) {
    logger.error('Failed to request help on funding search with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestMatchRequirementsSaga({ fundingMatchIds, promise }) {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');

  try {
    const response = yield all(
      fundingMatchIds.map((matchId) =>
        call(
          authApiService.get,
          routerService.generate('api.platform.funding_matches', {
            matchId,
          }),
        ),
      ),
    );

    const responseData = response.map(({ data }) => ({
      id: data.id,
      features: data.lender?.features || [],
      documents: data.lender?.documents || [],
    }));

    promise && promise.resolve(responseData);
  } catch (error) {
    logger.error('Failed to get matches requirements', error);
    promise && promise.reject(error);
  }
}

export default function* fundingSearchSaga() {
  return yield all([
    takeLatest(fundingSearchActive.request, requestFundingSearchActiveSaga),
    takeLatest(fundingSearchCompleted.request, requestFundingSearchCompletedSaga),
    takeLatest(fundingSearchShared.request, requestFundingSearchSharedSaga),
    takeLatest(fundingSearch.request, requestFundingSearchSaga),
    takeLatest(
      fundingSearchCompleteApplication.request,
      requestFundingSearchCompleteApplicationSaga,
    ),
    takeLatest(fundingSearchMatches.request, requestFundingSearchMatchesSaga),
    takeLatest(cancelFundingSearch.request, requestCancelFundingSearchSaga),
    takeLatest(cancelFundingSearchNewHub.request, requestCancelFundingSearchNewHubSaga),
    takeLatest(REQUEST_FUNDING_SEARCH_DOCUMENTS, requestFundingSearchDocumentsSaga),
    takeLatest(REQUEST_DOWNLOAD_PROFILE_ATTACHMENT, requestDownloadProfileAttachmentSaga),
    takeLatest(REQUEST_FUNDING_MATCHES_REQUIREMENTS, requestMatchRequirementsSaga),
    takeLatest(deleteBusinessProfile.request, requestDeleteBusinessProfileSaga),
    takeLatest(deleteBusinessProfileNewHub.request, requestDeleteBusinessProfileNewHubSaga),
    takeLatest(dismissFundingSearchBanner.request, requestFundingSearchBannerDismissSaga),
    takeLatest(requestFundingSearchHelp.request, requestFundingSearchRequestFundingSearchHelpSaga),
    contactDetailsSaga(),
    dueActivitiesCountSaga(),
    matchSaga(),
    offersSaga(),
    offersHistorySaga(),
    privacyToggleSaga(),
    searchFollowersSaga(),
    searchManagerSaga(),
  ]);
}
